import { useRef, useState, useEffect, useCallback } from "react"
import { useMutation } from "@tanstack/react-query"
import { useForm, Controller } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import PhoneInput from "react-phone-number-input"
import "react-phone-number-input/style.css"
import { paymentSchema } from "./schema"
import styles from "./index.module.css"
import { useLocation, useNavigate } from "react-router-dom"
import { updateBookingInFirestore, fetchBookingById } from "../../lib/actions"
import { countries } from "../../lib/constants"
import Select from "react-select"

export default function PaymentForm() {
    const location = useLocation()
    const navigate = useNavigate()
    const [loading, setLoading] = useState(false)
    const [selectedCountry, setSelectedCountry] = useState(null)
    const formDataRef = useRef(null)
    const collectJsConfigured = useRef(false)
    const [cardErrors, setCardErrors] = useState({
        ccnumber: "",
        ccexp: "",
        cvv: "",
    })

    const queryParams = new URLSearchParams(location.search)
    const bookingId = queryParams.get("bookingId")
    const { register, handleSubmit, formState: { errors }, control, setValue } = useForm({
        resolver: yupResolver(paymentSchema),
        defaultValues: {
            amount: 0
        }
    })

    useEffect(() => {
        if (!bookingId) {
            alert("Invalid booking reference. Please return to the booking page and try again.")
            return
        }
        const fetchBooking = async () => {
            try {
                const booking = await fetchBookingById(bookingId)
                setValue("amount", booking.totalCost)
            } catch (error) {
                alert("We couldn't find your booking details. Please return to the home page and start a new booking.")
            }
        }
        fetchBooking()
    }, [bookingId, navigate, setValue])

    const processPayment = async (paymentData) => {
        const response = await fetch(`https://us-central1-rico-booking-app.cloudfunctions.net/payment-handler`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(paymentData),
        })
        if (!response.ok) {
            throw new Error("Failed to process payment")
        }
        return response.json()
    }

    const processPaymentMutation = useMutation({
        mutationFn: processPayment,
    })

    const onSubmit = (formData) => {
        formDataRef.current = formData
        setLoading(true)
        window.CollectJS.startPaymentRequest()
    }

    const processPaymentToken = useCallback(async (response) => {
        const formData = formDataRef.current
        const paymentData = {
            paymentToken: response.token,
            orderDetails: {
                amount: formData.amount,
            },
            billingDetails: {
                firstName: formData.firstName,
                lastName: formData.lastName,
                company: formData.company,
                address1: formData.address1,
                address2: formData.address2,
                city: formData.city,
                state: formData.state,
                zip: formData.zip,
                country: formData.country,
                phone: formData.phone,
                email: formData.email,
            },
        }

        processPaymentMutation.mutate(paymentData, {
            onSuccess: async (data) => {
                if (data.response === "1") {
                    try {
                        await updateBookingInFirestore(bookingId, {
                            status: 'paid',
                            billingDetails: paymentData.billingDetails,
                            paymentDetails: {
                                status: 'completed',
                                message: data.message,
                                responseCode: data.response,
                                amount: paymentData.orderDetails.amount,
                                date: new Date(),
                            },
                        })
                        navigate("/success")
                    } catch (error) {
                        console.error("Error saving booking data:", error)
                        alert("Your payment was successful! However, we're having trouble updating your booking. Please contact our support team with your payment confirmation.")
                    }
                } else {
                    await updateBookingInFirestore(bookingId, {
                        status: 'pending',
                        billingDetails: paymentData.billingDetails,
                        paymentDetails: {
                            status: 'failed',
                            message: data.message,
                            responseCode: data.response,
                            amount: paymentData.orderDetails.amount,
                            date: new Date(),
                        },
                    })
                    alert(
                        data.response === "2"
                            ? `Payment was declined by your bank. Reason: ${data.message}`
                            : `We encountered an issue processing your payment. Reason: ${data.message}`
                    )
                }
            },
            onError: (error) => {
                console.error("Error processing payment:", error)
                alert("We're having trouble processing your payment. Please check your card details and try again.")
            },
        })
    }, [bookingId, navigate, processPaymentMutation])

    useEffect(() => {
        if (!window.CollectJS || collectJsConfigured.current) return // Skip if already configured
        if (!window.CollectJS) {
            console.error("❌ CollectJS is not available")
            return
        }

        window.CollectJS.configure({
            variant: "inline",
            styleSniffer: true,
            callback: (response) => {
                setLoading(true)
                if (response.error) {
                    setCardErrors({
                        ccnumber: response.error.field === "ccnumber" ? response.error.message : "",
                        ccexp: response.error.field === "ccexp" ? response.error.message : "",
                        cvv: response.error.field === "cvv" ? response.error.message : "",
                    })
                }
                else {
                    processPaymentToken(response)
                    setLoading(false)
                }
            },
            validationCallback: (field, valid, message) => {
                setCardErrors(prev => ({
                    ...prev,
                    [field]: valid ? "" : message || "Invalid input",
                }))
            },
            fields: {
                ccnumber: {
                    placeholder: "CC Number",
                    selector: "#ccnumber",
                },
                ccexp: {
                    placeholder: "CC Expiration",
                    selector: "#ccexp",
                },
                cvv: {
                    placeholder: "CVV",
                    selector: "#cvv",
                },
            },
        })

        collectJsConfigured.current = true
    }, [processPaymentToken])

    return (
        <section className="py-4">
            <div className="container">
                <form onSubmit={handleSubmit(onSubmit)}>
                    <h4 className="heading-text-primary">
                        <span className="material-symbols-outlined">
                            payments
                        </span> Billing Information
                    </h4>
                    <div className={styles.infoSection}>
                        <div className="row">
                            <div className="col">
                                <div className={`form-group`}>
                                    <label className={styles.label}>
                                        First Name
                                    </label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.firstName ? "is-invalid" : ""}`}
                                        placeholder="e.g. John"
                                        {...register("firstName")}
                                    />
                                    {errors.firstName && <div className="invalid-feedback">{errors.firstName.message}</div>}
                                </div>
                            </div>
                            <div className="col">
                                <div className={`form-group`}>
                                    <label className={styles.label}>
                                        Last Name
                                    </label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.lastName ? "is-invalid" : ""}`}
                                        placeholder="e.g. Doe"
                                        {...register("lastName")}
                                    />
                                    {errors.lastName && <div className="invalid-feedback">{errors.lastName.message}</div>}
                                </div>
                            </div>
                            <div className="col">
                                <div className={`form-group`}>
                                    <label className={styles.label}>
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        className={`form-control ${errors.email ? "is-invalid" : ""}`}
                                        placeholder="email@example.com"
                                        {...register("email")}
                                    />
                                    {errors.email && <div className="invalid-feedback">{errors.email.message}</div>}
                                </div>
                            </div>
                            <div className="col">
                                <div className={`form-group`}>
                                    <label className={styles.label}>
                                        Phone
                                    </label>
                                    <Controller
                                        name="phone"
                                        control={control}
                                        render={({ field }) => (
                                            <PhoneInput
                                                {...field}
                                                defaultCountry="US"
                                                placeholder="123 456 7890"
                                                className={`form-control ${styles.phoneInput} ${errors.phone ? "is-invalid" : ""}`}
                                                onChange={(value) => field.onChange(value)}
                                                value={field.value}
                                            />
                                        )}
                                    />
                                    {errors.phone && <div className="invalid-feedback">{errors.phone.message}</div>}
                                </div>
                            </div>
                        </div>

                        <div className="row mt-2">
                            <div className="col">
                                <div className={`form-group`}>
                                    <label className={styles.label}>
                                        Address 1
                                    </label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.address1 ? "is-invalid" : ""}`}
                                        {...register("address1")}
                                    />
                                    {errors.address1 && <div className="invalid-feedback">{errors.address1.message}</div>}
                                </div>
                            </div>
                            <div className="col">
                                <div className={`form-group`}>
                                    <label className={styles.label}>
                                        Address 2
                                    </label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.address2 ? "is-invalid" : ""}`}
                                        {...register("address2")}
                                    />
                                    {errors.address2 && <div className="invalid-feedback">{errors.address2.message}</div>}
                                </div>
                            </div>
                        </div>

                        <div className="row mt-2">
                            <div className="col">
                                <div className={`form-group`}>
                                    <label className={styles.label}>
                                        City
                                    </label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.city ? "is-invalid" : ""}`}
                                        {...register("city")}
                                        placeholder="Enter city name"
                                    />
                                    {errors.city && <div className="invalid-feedback">{errors.city.message}</div>}
                                </div>
                            </div>
                            <div className="col">
                                <div className={`form-group`}>
                                    <label className={styles.label}>
                                        State
                                    </label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.state ? "is-invalid" : ""}`}
                                        {...register("state")}
                                        placeholder="Enter state"
                                    />
                                    {errors.state && <div className="invalid-feedback">{errors.state.message}</div>}
                                </div>
                            </div>
                            <div className="col">
                                <div className={`form-group`}>
                                    <label className={styles.label}>
                                        ZIP
                                    </label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.zip ? "is-invalid" : ""}`}
                                        {...register("zip")}
                                        placeholder="Enter ZIP code (e.g., 12345)"
                                        maxLength="10"
                                    />
                                    {errors.zip && <div className="invalid-feedback">{errors.zip.message}</div>}
                                </div>
                            </div>
                            <div className="col">
                                <div className={`form-group`}>
                                    <label className={styles.label}>
                                        Country
                                    </label>
                                    <Controller
                                        name="country"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                {...field}
                                                options={countries}
                                                value={selectedCountry}
                                                onChange={(selectedOption) => {
                                                    field.onChange(selectedOption?.value || '')
                                                    setSelectedCountry(selectedOption)
                                                }}
                                                placeholder="Select Country"
                                                className={`${errors.country ? "is-invalid" : ""}`}
                                            />
                                        )}
                                    />
                                    {errors.country && <div className="invalid-feedback">{errors.country.message}</div>}
                                </div>
                            </div>
                        </div>

                        <div className="row my-2">
                            <div className="col">
                                <div className={`form-group`}>
                                    <label className={styles.label}>
                                        Company
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="e.g. Acme Corp"
                                        {...register("company")}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-5">
                        <h4 className="heading-text-primary">
                            <span className="material-symbols-outlined">
                                credit_card
                            </span> Card Information
                        </h4>
                    </div>
                    <div className={styles.infoSection}>
                        <div className="row mt-2">
                            <div className="col">
                                <div className={`form-group`}>
                                    <label className={styles.label}>
                                        Amount
                                    </label>
                                    <input
                                        type="number"
                                        className={`form-control ${errors.amount ? "is-invalid" : ""}`}
                                        {...register("amount")}
                                        disabled
                                    />
                                    {errors.amount && <div className="invalid-feedback">{errors.amount.message}</div>}
                                </div>
                            </div>
                            <div className="col">
                                <div className={`form-group ${cardErrors.ccnumber ? "has-error" : ""}`}>
                                    <label className={styles.label}>
                                        Card Number
                                    </label>
                                    <div id="ccnumber" className="collectjs-field" data-collect="cardNumber"></div>
                                    {cardErrors.ccnumber && <div className="text-danger small mt-1">{cardErrors.ccnumber}</div>}
                                </div>
                            </div>
                        </div>
                        <div className="row my-2">
                            <div className="col">
                                <div className={`form-group ${cardErrors.ccnumber ? "has-error" : ""}`}>
                                    <label className={styles.label}>
                                        Expiration Date
                                    </label>
                                    <div id="ccexp" className="collectjs-field" data-collect="expDate"></div>
                                    {cardErrors.ccexp && <div className="text-danger small mt-1">{cardErrors.ccexp}</div>}
                                </div>
                            </div>
                            <div className="col">
                                <div className={`form-group ${cardErrors.ccnumber ? "has-error" : ""}`}>
                                    <label className={styles.label}>
                                        Security Code (CVV)
                                    </label>
                                    <div id="cvv" className="collectjs-field" data-collect="cvv"></div>
                                    {cardErrors.cvv && <div className="text-danger small mt-1">{cardErrors.cvv}</div>}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={styles.buttonContainer}>
                        <button type="button" className="btn-secondary" onClick={() => navigate(-1, { state: location.state })}>Back to Summary</button>
                        <button type="submit" className="btn-primary" disabled={loading}>
                            {loading ? 'Processing Payment...' : 'Submit Payment'}
                        </button>
                    </div>
                </form>
            </div>
        </section>
    )
}